'use client';

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Database,
  Lightning,
  Terminal,
  Table,
  DownloadSimple,
  CaretLeft,
  CaretRight,
  ArrowsLeftRight,
  CaretUp,
  Minus,
  ShareNetwork,
} from '@phosphor-icons/react';
import { useUploadedFileStore } from '../../stores/uploadedFileStore';
import { useWorkflowStore } from '../../stores/workflowStore';
import { DiffResultView, type DiffResultViewRef } from './DiffResultView';
import { ShareDialog } from '../share/ShareDialog';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ExecutionResultItem {
  nodeId: string;
  label: string;
  data: Record<string, unknown>[];
  columns: string[];
  rowCount: number;
  metadata?: Record<string, unknown>;
  isDiffResult?: boolean;
  executionId?: string; // 执行ID，用于分享功能
}

interface DataAreaProps {
  executionResults: ExecutionResultItem[];
  executionLogs: string[];
  /** Called for regular result downloads. Diff results pass `exportOverride` with original-column data. */
  onDownloadResult?: (index: number, exportOverride?: { columns: string[]; rows: string[][] }) => void;
  /** 是否折叠 */
  isCollapsed?: boolean;
  /** 切换折叠状态 */
  onToggleCollapse?: () => void;
  /** 工作流名称（用于分享） */
  workflowName?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const INPUT_NODE_TYPES = new Set(['load_excel', 'load_csv', 'load_table', 'load_api', 'online_sheet']);

const PNG_DATA_URL_PREFIX = 'data:image/png;base64,';

/** 若为条码/PNG 图片（data URL 或纯 base64），返回可用于 <img src> 的字符串，否则返回 null */
function getCellImageSrc(value: unknown): string | null {
  if (value == null) return null;
  const s = typeof value === 'string' ? value.trim() : String(value);
  if (!s) return null;
  if (s.startsWith(PNG_DATA_URL_PREFIX)) return s;
  if (s.length > 50 && !s.includes(' ') && /^[A-Za-z0-9+/=]+$/.test(s)) {
    return PNG_DATA_URL_PREFIX + s;
  }
  return null;
}

function formatCellValue(value: unknown, t: (key: string) => string): string {
  if (value instanceof ArrayBuffer) return '[Binary]';
  if (ArrayBuffer.isView(value)) return '[Binary]';
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') {
    if (value.length > 100 && /[\x00-\x08\x0B\x0C\x0E-\x1F]/.test(value)) return '[Binary]';
    return value;
  }
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no');
  if (value instanceof Date) return value.toISOString().split('T')[0];
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if ('w' in obj && typeof obj.w === 'string') return obj.w;
    if ('v' in obj) {
      const val = obj.v;
      if (val === null || val === undefined) return '';
      if (typeof val === 'string') return val;
      if (typeof val === 'number') return String(val);
      if (val instanceof Date) return val.toISOString().split('T')[0];
      return String(val);
    }
    try {
      const s = JSON.stringify(value);
      return s.length > 60 ? s.substring(0, 60) + '…' : s;
    } catch {
      return '[Object]';
    }
  }
  return String(value);
}

// 每页行数（仅渲染当前页，降低内存与渲染压力）
const PAGE_SIZE = 500;

// ── Data Table ────────────────────────────────────────────────────────────────

export function DataTable({
  data,
  columns,
  rowOffset = 0,
}: {
  data: Record<string, unknown>[];
  columns: string[];
  /** 行号起始偏移（分页时用于显示全局行号） */
  rowOffset?: number;
}) {
  const { t } = useTranslation();

  if (columns.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-14 text-center px-6">
        <Table className="w-8 h-8 text-zinc-300 mb-2" />
        <p className="text-sm text-zinc-400">{t('nodes.preview.noData')}</p>
      </div>
    );
  }
  return (
    <table className="w-full text-xs border-collapse">
      <thead className="sticky top-0 z-10">
        <tr className="bg-zinc-50">
          <th className="w-10 px-3 py-2 text-right font-medium text-zinc-400 border-b border-r border-zinc-200 bg-zinc-50 select-none">#</th>
          {columns.map((col, colIdx) => (
            <th key={`${col}-${colIdx}`} className="px-3 py-2 text-left font-semibold text-zinc-600 border-b border-r border-zinc-200 whitespace-nowrap bg-zinc-50 last:border-r-0">
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((row, idx) => (
          <tr key={row.id != null ? String(row.id) : `${rowOffset}-${idx}`} className={`hover:bg-blue-50/30 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-zinc-50/40'}`}>
            <td className="px-3 py-1.5 text-zinc-400 border-b border-r border-zinc-100 text-right select-none tabular-nums">{rowOffset + idx + 1}</td>
            {columns.map((col, colIdx) => {
              const cellVal = row[col];
              const imgSrc = getCellImageSrc(cellVal);
              return (
                <td key={`${col}-${colIdx}`} className="px-3 py-1.5 text-zinc-700 border-b border-r border-zinc-100 last:border-r-0 max-w-[220px] align-middle" title={imgSrc ? '' : formatCellValue(cellVal, t)}>
                  {imgSrc ? (
                    <img src={imgSrc} alt="" className="max-h-[72px] max-w-[120px] w-auto h-auto object-contain block" />
                  ) : (
                    <span className="whitespace-nowrap truncate block">{formatCellValue(cellVal, t)}</span>
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────

function EmptyState({ icon: Icon, title, description, animated = false }: { icon: React.ElementType; title: string; description: string; animated?: boolean }) {
  const el = (
    <div className="w-14 h-14 rounded-2xl bg-zinc-100 flex items-center justify-center mb-3">
      <Icon className="w-7 h-7 text-zinc-300" />
    </div>
  );
  return (
    <div className="flex flex-col items-center justify-center py-14 text-center px-6">
      {animated ? (
        <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }}>{el}</motion.div>
      ) : el}
      <p className="text-sm text-zinc-500 mb-1">{title}</p>
      <p className="text-xs text-zinc-400">{description}</p>
    </div>
  );
}

// ── Tab Scrollbar ─────────────────────────────────────────────────────────────

function TabBar({ children }: { children: React.ReactNode }) {
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const onScroll = (el: HTMLDivElement | null) => {
    if (!el) return;
    setShowLeft(el.scrollLeft > 4);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -100, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 100, behavior: 'smooth' });
    }
  };

  return (
    <div className="relative flex items-center flex-1 min-w-0 overflow-hidden">
      {showLeft && (
        <button 
          onClick={scrollLeft}
          className="absolute left-0 z-10 h-full px-1 bg-gradient-to-r from-zinc-50 to-transparent text-zinc-400 hover:text-zinc-700"
        >
          <CaretLeft className="w-3 h-3" weight="bold" />
        </button>
      )}
      <div
        ref={(el) => { scrollRef.current = el; onScroll(el); }}
        onScroll={(e) => onScroll(e.currentTarget)}
        className="flex items-center gap-0.5 overflow-x-auto scrollbar-none px-1"
        style={{ scrollbarWidth: 'none' }}
      >
        {children}
      </div>
      {showRight && (
        <button 
          onClick={scrollRight}
          className="absolute right-0 z-10 h-full px-1 bg-gradient-to-l from-zinc-50 to-transparent text-zinc-400 hover:text-zinc-700"
        >
          <CaretRight className="w-3 h-3" weight="bold" />
        </button>
      )}
    </div>
  );
}

// ── Section Divider in Tab Bar ────────────────────────────────────────────────

function TabDivider() {
  return <div className="w-px h-4 bg-zinc-200 mx-1 flex-shrink-0" />;
}

// ── Main Component ────────────────────────────────────────────────────────────

export function DataArea({ executionResults, executionLogs, onDownloadResult, isCollapsed = false, onToggleCollapse, workflowName }: DataAreaProps) {
  const { t } = useTranslation();
  const { files } = useUploadedFileStore();
  const { nodes } = useWorkflowStore();

  // Ref to the active DiffResultView so we can pull export data that reflects cell decisions
  const diffViewRef = useRef<DiffResultViewRef | null>(null);

  // 分享对话框状态
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [activeExecutionId, setActiveExecutionId] = useState<string | null>(null);

  // Build ordered list of input nodes that have an uploaded file
  const inputTabs = useMemo(() => {
    const inputNodes = nodes.filter((n) => INPUT_NODE_TYPES.has(n.type ?? ''));
    return inputNodes
      .filter((n) => !!files[n.id])
      .map((n, idx) => ({
        id: `input_${n.id}`,
        nodeId: n.id,
        label: inputNodes.length > 1 ? t('nodes.dataArea.inputNumber', { number: idx + 1 }) : t('nodes.dataArea.inputData'),
        fileName: files[n.id]!.name,
        rowCount: files[n.id]!.rowCount,
        data: files[n.id]!.data,
        columns: files[n.id]!.columns,
      }));
  }, [nodes, files, t]);

  // Build result tabs
  const resultTabs = useMemo(() =>
    executionResults.map((r, idx) => ({
      id: `result_${idx}`,
      index: idx,
      label: executionResults.length > 1 ? t('nodes.dataArea.resultNumber', { number: idx + 1 }) : t('nodes.preview.executionResult'),
      rowCount: r.rowCount,
      data: r.data,
      columns: r.columns,
      metadata: r.metadata,
      isDiffResult: r.isDiffResult || r.metadata?.isDiffResult === true,
      executionId: r.executionId,
    })),
    [executionResults, t]
  );

  // Default active tab（生产环境不使用 'log' 作为默认）
  const defaultTab = inputTabs[0]?.id ?? (import.meta.env.DEV ? 'log' : 'result_empty');
  const [activeTab, setActiveTab] = useState<string>(defaultTab);

  // 处理分享按钮点击
  const handleShare = useCallback(() => {
    const activeResult = resultTabs.find(t => t.id === activeTab);
    if (activeResult?.executionId) {
      setActiveExecutionId(activeResult.executionId);
      setIsShareDialogOpen(true);
    }
  }, [activeTab, resultTabs]);

  // 分页：仅对输入/结果表格生效，切换 tab 时重置
  const [pageIndex, setPageIndex] = useState(0);

  // Auto-switch to first result when execution finishes；每次运行后重置到第一页
  useEffect(() => {
    if (resultTabs.length > 0) setActiveTab(resultTabs[0].id);
    setPageIndex(0);
  }, [executionResults]); // eslint-disable-line react-hooks/exhaustive-deps

  // If active tab was removed (e.g. file cleared), fall back（生产环境不包含 'log'）
  useEffect(() => {
    const allIds = [...inputTabs.map(t => t.id), ...resultTabs.map(t => t.id), ...(import.meta.env.DEV ? ['log'] : [])];
    if (!allIds.includes(activeTab)) {
      setActiveTab(allIds[0] ?? (import.meta.env.DEV ? 'log' : 'result_empty'));
    }
  }, [inputTabs, resultTabs, activeTab]);

  // 切换 Tab 时重置到第一页
  useEffect(() => {
    setPageIndex(0);
  }, [activeTab]);

  const activeInput  = inputTabs.find(t => t.id === activeTab);
  const activeResult = resultTabs.find(t => t.id === activeTab);
  const activeResultIndex = activeResult ? activeResult.index : -1;

  // 处理 Tab 点击：如果处于折叠状态，先展开；切换 tab 时重置分页
  const handleTabClick = (tabId: string) => {
    if (isCollapsed && onToggleCollapse) {
      onToggleCollapse();
    }
    setActiveTab(tabId);
    setPageIndex(0);
  };

  // 当前 tab 对应的总行数（输入或结果）
  const currentTotalRows = activeInput ? activeInput.data.length : activeResult ? activeResult.data.length : 0;
  const totalPages = Math.max(1, Math.ceil(currentTotalRows / PAGE_SIZE));
  const safePageIndex = Math.min(pageIndex, totalPages - 1);
  const pageStart = safePageIndex * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, currentTotalRows);
  const currentPageData =
    activeInput
      ? activeInput.data.slice(pageStart, pageEnd)
      : activeResult && !activeResult.isDiffResult
        ? activeResult.data.slice(pageStart, pageEnd)
        : [];
  const currentPageDataDiff =
    activeResult?.isDiffResult ? activeResult.data.slice(pageStart, pageEnd) : [];
  const showPagination = (activeInput != null || (activeResult != null && !activeResult.isDiffResult)) && currentTotalRows > PAGE_SIZE;
  const showPaginationDiff = activeResult?.isDiffResult && currentTotalRows > PAGE_SIZE;


  return (
    <div className="flex flex-col h-full bg-white border-t border-zinc-200">

      {/* ── Tab Bar ── */}
      <div className="flex items-center h-9 border-b border-zinc-200 bg-zinc-50/60 flex-shrink-0 overflow-hidden">
        <TabBar>
          {/* ── Input tabs ── */}
          {inputTabs.length > 0 ? (
            inputTabs.map((tab) => (
              <TabButton
                key={tab.id}
                id={tab.id}
                active={activeTab === tab.id}
                onClick={() => handleTabClick(tab.id)}
                icon={Database}
                label={tab.label}
                badge={tab.rowCount}
                badgeColor="bg-blue-100 text-blue-700"
                title={tab.fileName}
              />
            ))
          ) : (
            <TabButton
              id="input_empty"
              active={activeTab === 'input_empty'}
              onClick={() => handleTabClick('input_empty')}
              icon={Database}
              label={t('nodes.dataArea.inputData')}
              badgeColor="bg-blue-100 text-blue-700"
            />
          )}

          {/* ── Divider ── */}
          <TabDivider />

          {/* ── Result tabs ── */}
          {resultTabs.length > 0 ? (
            resultTabs.map((tab) => (
              <TabButton
                key={tab.id}
                id={tab.id}
                active={activeTab === tab.id}
                onClick={() => handleTabClick(tab.id)}
                icon={tab.isDiffResult ? ArrowsLeftRight : Lightning}
                label={tab.label}
                badge={tab.rowCount}
                badgeColor={tab.isDiffResult ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'}
              />
            ))
          ) : (
            <TabButton
              id="result_empty"
              active={activeTab === 'result_empty'}
              onClick={() => handleTabClick('result_empty')}
              icon={Lightning}
              label={t('nodes.preview.executionResult')}
              badgeColor="bg-amber-100 text-amber-700"
            />
          )}

          {/* ── Divider ── */}
          <TabDivider />

          {/* ── Log tab ── 仅在开发环境显示 */}
          {import.meta.env.DEV && (
            <TabButton
              id="log"
              active={activeTab === 'log'}
              onClick={() => handleTabClick('log')}
              icon={Terminal}
              label={t('nodes.dataArea.logs')}
              badge={executionLogs.length > 0 ? executionLogs.length : undefined}
              badgeColor="bg-zinc-100 text-zinc-600"
            />
          )}
        </TabBar>

        {/* ── Right-side: 上一页/下一页（行数信息左侧）+ 行数信息 + 下载/折叠 ── */}
        <div className="flex items-center gap-2 pr-2 pl-1 flex-shrink-0">
          {/* 分页按钮：仅在当前为输入或结果 tab 且行数超过一页时显示 */}
          {(showPagination || showPaginationDiff) && (
            <div className="flex items-center gap-0.5 border border-zinc-200 rounded-md bg-white overflow-hidden">
              <button
                type="button"
                disabled={safePageIndex <= 0}
                onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                className="flex items-center gap-1 px-2 py-1 text-[10px] text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title={t('nodes.dataArea.prevPage')}
              >
                <CaretLeft className="w-3 h-3" weight="bold" />
                {t('nodes.dataArea.prevPage')}
              </button>
              <span className="h-4 w-px bg-zinc-200" />
              <button
                type="button"
                disabled={safePageIndex >= totalPages - 1}
                onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
                className="flex items-center gap-1 px-2 py-1 text-[10px] text-zinc-600 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title={t('nodes.dataArea.nextPage')}
              >
                {t('nodes.dataArea.nextPage')}
                <CaretRight className="w-3 h-3" weight="bold" />
              </button>
            </div>
          )}

          {activeInput && (
            <span className="text-[10px] text-zinc-400 tabular-nums whitespace-nowrap">
              {showPagination
                ? t('nodes.dataArea.pageRange', {
                    start: (pageStart + 1).toLocaleString(),
                    end: pageEnd.toLocaleString(),
                    total: currentTotalRows.toLocaleString(),
                  })
                : `${activeInput.rowCount.toLocaleString()} ${t('nodes.rows')}`}
            </span>
          )}
          {activeResult && (
            <>
              <span className="text-[10px] text-emerald-600 font-medium tabular-nums whitespace-nowrap">
                {showPagination || showPaginationDiff
                  ? t('nodes.dataArea.pageRange', {
                      start: (pageStart + 1).toLocaleString(),
                      end: pageEnd.toLocaleString(),
                      total: currentTotalRows.toLocaleString(),
                    })
                  : t('nodes.dataArea.totalRows', { count: activeResult.rowCount })}
              </span>
              {/* 分享按钮 - 在下载按钮左侧 */}
              {activeResult?.executionId && (
                <button
                  onClick={handleShare}
                  className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-zinc-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors border border-zinc-200 hover:border-blue-200"
                  title="分享结果"
                >
                  <ShareNetwork className="w-3 h-3" weight="bold" />
                  分享
                </button>
              )}
              {onDownloadResult && (
                <button
                  onClick={() => {
                    const exportOverride =
                      activeResult?.isDiffResult
                        ? diffViewRef.current?.getExportData()
                        : undefined;
                    onDownloadResult(activeResultIndex, exportOverride);
                  }}
                  className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-zinc-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors border border-zinc-200 hover:border-emerald-200"
                >
                  <DownloadSimple className="w-3 h-3" weight="bold" />
                  {t('nodes.preview.download')}
                </button>
              )}
            </>
          )}
          {import.meta.env.DEV && activeTab === 'log' && executionLogs.length > 0 && (
            <span className="text-[10px] text-zinc-400 tabular-nums whitespace-nowrap">
              {executionLogs.length} {t('nodes.dataArea.logEntries')}
            </span>
          )}
          
          {/* 折叠/展开按钮 */}
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 rounded-md transition-colors border border-zinc-200 ml-1"
              title={isCollapsed ? t('nodes.dataArea.expand') : t('nodes.dataArea.collapse')}
            >
              {isCollapsed ? (
                <>
                  <CaretUp className="w-3 h-3" weight="bold" />
                  {/* 展开 */}
                </>
              ) : (
                <>
                  <Minus className="w-3 h-3" weight="bold" />
                  {/* 折叠 */}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* ── Tab Content ── */}
      <div className={`flex-1 min-h-0 overflow-auto ${isCollapsed ? 'hidden' : ''}`}>
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
            className="h-full"
          >
            {/* Input tab content：仅渲染当前页数据 */}
            {activeInput ? (
              <DataTable
                data={currentPageData}
                columns={activeInput.columns}
                rowOffset={pageStart}
              />
            ) : activeTab.startsWith('input_') ? (
              <EmptyState icon={Database} title={t('nodes.dataArea.noInputData')} description={t('nodes.dataArea.addInputHint')} animated />
            ) : null}

            {/* Result tab content：仅渲染当前页数据 */}
            {activeResult ? (
              activeResult.isDiffResult ? (
                <DiffResultView
                  ref={diffViewRef}
                  data={currentPageDataDiff}
                  columns={activeResult.columns}
                  metadata={activeResult.metadata}
                />
              ) : (
                <DataTable
                  data={currentPageData}
                  columns={activeResult.columns}
                  rowOffset={pageStart}
                />
              )
            ) : (activeTab === 'result_empty' || activeTab.startsWith('result_')) && !activeResult ? (
              <EmptyState icon={Lightning} title={t('nodes.preview.waiting')} description={t('nodes.dataArea.clickRun')} />
            ) : null}

            {/* Log tab content - 仅在开发环境显示 */}
            {import.meta.env.DEV && activeTab === 'log' && (
              executionLogs.length > 0 ? (
                <div className="min-h-full bg-zinc-950 p-3 font-mono text-xs leading-5 text-zinc-300">
                  {executionLogs.map((line, idx) => (
                    <div key={idx} className="flex gap-3 py-0.5">
                      <span className="text-zinc-600 select-none tabular-nums w-6 text-right flex-shrink-0">{idx + 1}</span>
                      <span className={
                        line.includes('[ERROR]')   ? 'text-red-400' :
                        line.includes('[SUCCESS]') ? 'text-emerald-400' :
                        line.includes('[WARN]')    ? 'text-amber-400' :
                        'text-zinc-300'
                      }>{line}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState icon={Terminal} title={t('nodes.dataArea.noLogs')} description={t('nodes.dataArea.runToSeeLogs')} />
              )
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* 分享对话框 */}
      <ShareDialog
        isOpen={isShareDialogOpen}
        onClose={() => setIsShareDialogOpen(false)}
        workflowExecutionId={activeExecutionId || ''}
        workflowName={workflowName || ''}
      />
    </div>
  );
}

// ── Tab Button ────────────────────────────────────────────────────────────────

function TabButton({
  id,
  active,
  onClick,
  icon: Icon,
  label,
  badge,
  badgeColor,
  title,
}: {
  id: string;
  active: boolean;
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  badge?: number | null;
  badgeColor: string;
  title?: string;
}) {
  void id;
  return (
    <button
      onClick={onClick}
      title={title}
      className={[
        'flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap flex-shrink-0',
        active
          ? 'bg-white text-zinc-900 shadow-sm border border-zinc-200'
          : 'text-zinc-500 hover:text-zinc-700 hover:bg-white/60',
      ].join(' ')}
    >
      <Icon className="w-3.5 h-3.5" weight={active ? 'fill' : 'regular'} />
      {label}
      {badge !== null && badge !== undefined && (
        <span className={`px-1.5 py-px rounded text-[10px] font-semibold ${badgeColor}`}>
          {badge.toLocaleString()}
        </span>
      )}
    </button>
  );
}
